import { computed, effect, inject, Injectable, signal } from '@angular/core'
import { loggedInRedirectUri, loggedOutRedirectUri } from '@core/auth/auth-redirect'
import {
  KEYCLOAK_EVENT_SIGNAL,
  KeycloakEventType,
  ReadyArgs,
  typeEventArgs,
} from 'keycloak-angular'
import Keycloak from 'keycloak-js'
import { UploadedLayersService } from './layers-uploaded.service'
import { ToasterService } from './toaster.service'
import { TokenRefreshService } from './token-refresh.service'

type UserInfo = {
  email: string
  name: string
}

@Injectable({
  providedIn: 'root',
})
export class SessionManagerService {
  private readonly _keycloak = inject(Keycloak)
  private readonly _toasterService = inject(ToasterService)
  private readonly _uploadedLayersService = inject(UploadedLayersService)
  private readonly _tokenRefresh = inject(TokenRefreshService)
  private readonly _keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL)

  userInfo$ = signal<UserInfo | null>(null)
  isLoggedIn$ = signal<boolean>(false)
  hasIntegrationAdminAttribute$ = computed(() => this._keycloak.hasRealmRole('integration_admin'))

  constructor() {
    effect(() => {
      const keycloakEvent = this._keycloakSignal()
      if (!keycloakEvent) return

      switch (keycloakEvent.type) {
        case KeycloakEventType.Ready:
          this._handleReady(typeEventArgs<ReadyArgs>(keycloakEvent.args))
          break
        case KeycloakEventType.AuthRefreshSuccess:
          this.isLoggedIn$.set(true)
          break
        case KeycloakEventType.AuthLogout:
          this.isLoggedIn$.set(false)
          break
      }
    })
  }

  private _handleReady(isLoggedIn: boolean) {
    this.isLoggedIn$.set(isLoggedIn)

    if (isLoggedIn) {
      this.getUserInfo()
      // Keep the access token alive in the background for this session.
      this._tokenRefresh.start()
    }

    this._consumeAuthFlags()
  }

  /** Show the login / logout toaster once, then drop the flag from the URL. */
  private _consumeAuthFlags() {
    const urlParams = new URLSearchParams(window.location.search)
    const user = this.userInfo$()

    if (urlParams.has('loggedOut')) {
      this._toasterService.openSnackBar('You have successfully logged out.')
    } else if (urlParams.has('loggedIn') && user) {
      this._toasterService.openSnackBar(`User connected. Welcome ${user.name}`)
    }

    const url = new URL(window.location.href)
    url.searchParams.delete('loggedOut')
    url.searchParams.delete('loggedIn')
    window.history.replaceState({}, '', url.toString())
  }

  getUserInfo() {
    const email = this._keycloak.tokenParsed?.email || ''
    const firstname = this._keycloak.tokenParsed?.given_name || ''
    const lastname = this._keycloak.tokenParsed?.family_name || ''
    const name = `${firstname} ${lastname}`.trim()

    this.userInfo$.set({ email, name })
  }

  login() {
    this._keycloak.login({ redirectUri: loggedInRedirectUri() })
  }

  logout() {
    this._keycloak.logout({ redirectUri: loggedOutRedirectUri() })
  }

  editAccount() {
    const accountUrl = `${this._keycloak.authServerUrl}/realms/${this._keycloak.realm}/account`
    window.location.href = accountUrl
  }

  sizeAccount() {
    if (this.isLoggedIn$()) {
      this._uploadedLayersService.checkSizeAccount()
    }
  }
}
